import { useEffect, useId, useRef, useState } from "react";
import { useAnalytics } from "../../../lib/analytics";
import { useClientTranslations } from "../../../lib/i18n/client";
import {
	createJourneyBooking,
	createMockBooking,
	generateMockAvailability,
	getBulkAvailability,
} from "../../../services/journeyBookingService";
import type {
	AvailabilitySlot,
	BulkAvailabilityDay,
	ScheduledMeeting,
} from "../../../types/booking";
import type { JourneyTask, JourneyType } from "../../../types/journey";
import styles from "./JourneyBookingModal.module.css";

const IS_DEMO = !import.meta.env.PROD;
const MOCK_PROVIDER_ID = "provider-seed-001";
const ENABLE_MOCK_PROVIDER =
	import.meta.env.VITE_ENABLE_MOCK_PROVIDER === "true";

export interface JourneyBookingModalProps {
	open: boolean;
	task: JourneyTask;
	journeyType: JourneyType;
	locale: string;
	patientUserId: string;
	providerId?: string;
	onClose: () => void;
	onBookingComplete: (meeting: ScheduledMeeting) => void;
}

interface FlatSlot {
	date: string;
	slot: AvailabilitySlot;
	key: string;
}

function formatDate(iso: string): string {
	return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
		weekday: "long",
		month: "long",
		day: "numeric",
		year: "numeric",
	});
}

function formatTime(t: string): string {
	const [h, m] = t.split(":").map(Number);
	const d = new Date();
	d.setHours(h, m, 0, 0);
	return d.toLocaleTimeString(undefined, {
		hour: "numeric",
		minute: "2-digit",
	});
}

function toLocalYmd(d: Date): string {
	const y = d.getFullYear();
	const m = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	return `${y}-${m}-${day}`;
}

function getNext7Days(): string[] {
	const days: string[] = [];
	for (let i = 0; i < 7; i++) {
		const d = new Date();
		d.setDate(d.getDate() + i);
		days.push(toLocalYmd(d));
	}
	return days;
}

function ClockIcon() {
	return (
		<svg
			className={styles.detailIcon}
			viewBox="0 0 20 20"
			fill="none"
			aria-hidden="true"
		>
			<circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5" />
			<path
				d="M10 6v4l2.5 1.5"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

function LocationIcon() {
	return (
		<svg
			className={styles.detailIcon}
			viewBox="0 0 20 20"
			fill="none"
			aria-hidden="true"
		>
			<path
				d="M10 18s-6-5.686-6-9a6 6 0 1 1 12 0c0 3.314-6 9-6 9z"
				stroke="currentColor"
				strokeWidth="1.5"
			/>
			<circle cx="10" cy="9" r="2" stroke="currentColor" strokeWidth="1.5" />
		</svg>
	);
}

function ContactIcon() {
	return (
		<svg
			className={styles.detailIcon}
			viewBox="0 0 20 20"
			fill="none"
			aria-hidden="true"
		>
			<circle cx="10" cy="7" r="3" stroke="currentColor" strokeWidth="1.5" />
			<path
				d="M4 17c0-3.314 2.686-6 6-6s6 2.686 6 6"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
			/>
		</svg>
	);
}

function AlertCircleIcon() {
	return (
		<svg
			className={styles.prereqIconSvg}
			viewBox="0 0 20 20"
			fill="none"
			aria-hidden="true"
		>
			<circle cx="10" cy="10" r="8.5" stroke="#FF8C00" strokeWidth="1.2" />
			<path
				d="M10 6.5v4.5"
				stroke="#FF8C00"
				strokeWidth="1.4"
				strokeLinecap="round"
			/>
			<circle cx="10" cy="13.5" r="0.8" fill="#FF8C00" />
		</svg>
	);
}

function CheckCircleIcon() {
	return (
		<svg
			className={styles.prereqIconSvg}
			viewBox="0 0 20 20"
			fill="none"
			aria-hidden="true"
		>
			<circle cx="10" cy="10" r="8.5" stroke="#FF8C00" strokeWidth="1.2" />
			<path
				d="M6.5 10l2.5 2.5 4.5-5"
				stroke="#FF8C00"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

const MOCK_PREREQS = [
	{
		id: "prereq-1",
		titleKey: "booking.prereqBloodWork",
		statusKey: "booking.prereqBloodWorkStatus",
		alert: true,
	},
	{
		id: "prereq-2",
		titleKey: "booking.prereqQuestionnaire",
		statusKey: "booking.prereqQuestionnaireStatus",
		alert: false,
	},
	{
		id: "prereq-3",
		titleKey: "booking.prereqBloodWork",
		statusKey: "booking.prereqBloodWorkStatus",
		alert: false,
	},
];

export function JourneyBookingModal({
	open,
	task,
	journeyType,
	locale,
	patientUserId,
	providerId,
	onClose,
	onBookingComplete,
}: JourneyBookingModalProps) {
	const { t } = useClientTranslations("journey");
	const { logEvent } = useAnalytics();
	const headingId = useId();

	const effectiveProviderId =
		providerId ?? (ENABLE_MOCK_PROVIDER ? MOCK_PROVIDER_ID : undefined);

	const [days, setDays] = useState<BulkAvailabilityDay[]>([]);
	const [selectedKey, setSelectedKey] = useState("");
	const [notes, setNotes] = useState("");
	const [loading, setLoading] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState("");
	const [booked, setBooked] = useState(false);

	const hasLoaded = useRef(false);
	const loadedForProviderId = useRef<string | undefined>(undefined);

	const flatSlots: FlatSlot[] = days.flatMap((d) =>
		d.slots
			.filter((s) => s.available)
			.map((s) => ({ date: d.date, slot: s, key: `${d.date}_${s.id}` })),
	);

	const selected = flatSlots.find((f) => f.key === selectedKey) ?? null;

	useEffect(() => {
		if (!open) {
			setDays([]);
			setSelectedKey("");
			setNotes("");
			setLoading(false);
			setSubmitting(false);
			setError("");
			setBooked(false);
			hasLoaded.current = false;
			loadedForProviderId.current = undefined;
			return;
		}
		if (
			hasLoaded.current &&
			loadedForProviderId.current === effectiveProviderId
		)
			return;
		hasLoaded.current = true;
		loadedForProviderId.current = effectiveProviderId;
		setSelectedKey("");
		setError("");

		logEvent("journey_booking_modal_open", undefined, {
			taskId: task.id,
			journeyType,
		});

		const next7 = getNext7Days();
		const startDate = next7[0];
		const endDate = next7[next7.length - 1];
		setLoading(true);

		if (IS_DEMO) {
			const result = generateMockAvailability(startDate, endDate);
			setDays(result);
			const first = result
				.flatMap((d) =>
					d.slots
						.filter((s) => s.available)
						.map((s) => ({ date: d.date, slot: s })),
				)
				.at(0);
			if (first) setSelectedKey(`${first.date}_${first.slot.id}`);
			setLoading(false);
		} else if (!effectiveProviderId) {
			setError(t("booking.errorNoProvider"));
			setLoading(false);
		} else {
			getBulkAvailability(effectiveProviderId, startDate, endDate)
				.then((result) => {
					setDays(result);
					const first = result
						.flatMap((d) =>
							d.slots
								.filter((s) => s.available)
								.map((s) => ({ date: d.date, slot: s })),
						)
						.at(0);
					if (first) setSelectedKey(`${first.date}_${first.slot.id}`);
				})
				.catch((err: unknown) => {
					console.warn("[JourneyBookingModal] getBulkAvailability error:", err);
					setError(t("booking.errorLoadSlots"));
				})
				.finally(() => setLoading(false));
		}
	}, [open, effectiveProviderId, task.id, journeyType, logEvent, t]);

	useEffect(() => {
		if (!open) return;
		const handleKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		window.addEventListener("keydown", handleKey);
		return () => window.removeEventListener("keydown", handleKey);
	}, [open, onClose]);

	if (!open) return null;

	async function handleBook() {
		if (!selected || submitting) return;
		setSubmitting(true);
		setError("");

		if (!IS_DEMO && !effectiveProviderId) {
			setError(t("booking.errorNoProvider"));
			setSubmitting(false);
			return;
		}

		const trimmedNotes = notes.trim();
		const bookingData = {
			providerUserId: effectiveProviderId ?? "demo-provider",
			patientUserId,
			availabilityId: selected.slot.id,
			scheduledDate: selected.date,
			startTime: selected.slot.startTime,
			endTime: selected.slot.endTime,
			duration: selected.slot.duration,
			title: task.title,
			...(trimmedNotes ? { notes: trimmedNotes } : {}),
			journeyTaskId: task.id,
			journeyType,
			journeyLocale: locale,
		};

		try {
			const meeting = IS_DEMO
				? createMockBooking(bookingData)
				: await createJourneyBooking(bookingData);
			logEvent("journey_booking_confirmed", undefined, {
				taskId: task.id,
				meetingId: meeting.id,
				journeyType,
			});
			setBooked(true);
			onBookingComplete(meeting);
		} catch (err: unknown) {
			console.warn("[JourneyBookingModal] createJourneyBooking error:", err);
			setError(t("booking.errorSubmit"));
			logEvent("journey_booking_error", undefined, {
				taskId: task.id,
				journeyType,
			});
		} finally {
			setSubmitting(false);
		}
	}

	if (booked) {
		return (
			<>
				<div className={styles.backdrop} />
				<div className={styles.wrapper}>
					<div role="dialog" aria-modal="true" className={styles.dialog}>
						<div className={styles.successContent}>
							<div className={styles.successIcon}>✓</div>
							<p className={styles.successTitle}>{t("booking.successTitle")}</p>
							<p className={styles.successSub}>{t("booking.successSub")}</p>
							<button
								type="button"
								className={styles.bookButton}
								onClick={onClose}
							>
								{t("booking.done")}
							</button>
						</div>
					</div>
				</div>
			</>
		);
	}

	return (
		<>
			<button
				type="button"
				className={styles.backdrop}
				onClick={onClose}
				aria-label={t("booking.close")}
			/>
			<div className={styles.wrapper}>
				<div
					role="dialog"
					aria-modal="true"
					aria-labelledby={headingId}
					className={styles.dialog}
				>
					<div className={styles.header}>
						<div className={styles.headerText}>
							<h2 id={headingId} className={styles.heading}>
								{task.title}
							</h2>
							<p className={styles.subtitle}>{task.description}</p>
						</div>
						<button
							type="button"
							className={styles.closeButton}
							onClick={onClose}
							aria-label={t("booking.close")}
						>
							<svg
								className={styles.closeIcon}
								viewBox="0 0 14 14"
								fill="none"
								aria-hidden="true"
							>
								<path
									d="M1 1l12 12M13 1L1 13"
									stroke="currentColor"
									strokeWidth="1.5"
									strokeLinecap="round"
								/>
							</svg>
						</button>
					</div>

					<div className={styles.body}>
						{loading ? (
							<div className={styles.centered}>
								<div className={styles.spinner} />
								<p className={styles.emptyText}>{t("booking.loadingSlots")}</p>
							</div>
						) : error && flatSlots.length === 0 ? (
							<div className={styles.centered}>
								<p className={styles.errorText}>{error}</p>
							</div>
						) : flatSlots.length === 0 ? (
							<div className={styles.centered}>
								<p className={styles.emptyText}>{t("booking.noSlots")}</p>
							</div>
						) : (
							<>
								<div className={styles.section}>
									<h3 className={styles.sectionHeading}>
										{t("booking.requiredBeforeBooking")}
									</h3>
									<div className={styles.prereqList}>
										{MOCK_PREREQS.map((prereq) => (
											<div
												key={prereq.id}
												className={`${styles.prereqCard} ${prereq.alert ? styles.prereqCardAlert : ""}`}
											>
												{prereq.alert ? (
													<AlertCircleIcon />
												) : (
													<CheckCircleIcon />
												)}
												<div className={styles.prereqText}>
													<span className={styles.prereqTitle}>
														{t(prereq.titleKey)}
													</span>
													<span className={styles.prereqSub}>
														{t(prereq.statusKey)}
													</span>
												</div>
											</div>
										))}
									</div>
								</div>

								<div className={styles.section}>
									<h3 className={styles.sectionHeading}>
										{t("booking.selectDate")}
									</h3>
									<div className={styles.slotList}>
										{flatSlots.slice(0, 3).map(({ date, slot, key }) => {
											const isSelected = selectedKey === key;
											return (
												<button
													key={key}
													type="button"
													className={`${styles.slotCard} ${isSelected ? styles.slotCardSelected : ""}`}
													onClick={() => {
														setSelectedKey(key);
														logEvent(
															"journey_booking_slot_selected",
															undefined,
															{
																taskId: task.id,
																date,
																slotId: slot.id,
															},
														);
													}}
													aria-pressed={isSelected}
												>
													<div
														className={`${styles.radio} ${isSelected ? styles.radioSelected : ""}`}
													/>
													<div className={styles.slotText}>
														<span className={styles.slotDate}>
															{formatDate(date)}
														</span>
														<span className={styles.slotTime}>
															{formatTime(slot.startTime)} -{" "}
															{formatTime(slot.endTime)}
														</span>
													</div>
												</button>
											);
										})}
									</div>
								</div>

								<div className={styles.section}>
									<h3 className={styles.sectionHeading}>
										{t("booking.appointmentDetails")}
									</h3>
									<div className={styles.detailsCard}>
										<div className={styles.detailRow}>
											<ClockIcon />
											<div className={styles.detailContent}>
												<span className={styles.detailLabel}>
													{t("booking.duration")}
												</span>
												<span className={styles.detailValue}>
													{t("booking.minutes", {
														count: selected?.slot.duration ?? 60,
													})}
												</span>
											</div>
										</div>
										<div className={styles.detailRow}>
											<LocationIcon />
											<div className={styles.detailContent}>
												<span className={styles.detailLabel}>
													{t("booking.location")}
												</span>
												<span className={styles.detailValue}>
													{t("booking.locationName")}
												</span>
												<span className={styles.detailValueSub}>
													{t("booking.locationAddress")}
												</span>
											</div>
										</div>
										<div className={styles.detailRow}>
											<ContactIcon />
											<div className={styles.detailContent}>
												<span className={styles.detailLabel}>
													{t("booking.contactPerson")}
												</span>
												<span className={styles.detailValue}>
													{t("booking.contactName")}
												</span>
												<span className={styles.detailValueSub}>
													{t("booking.contactTitle")}
												</span>
											</div>
										</div>
									</div>
								</div>

								<div className={styles.section}>
									<h3 className={styles.sectionHeading}>
										{t("booking.notesLabel")}
									</h3>
									<textarea
										className={styles.notesInput}
										aria-label={t("booking.notesLabel")}
										value={notes}
										onChange={(e) => setNotes(e.target.value)}
										placeholder={t("booking.notesPlaceholder")}
										rows={3}
									/>
								</div>

								{error && <p className={styles.errorText}>{error}</p>}

								<button
									type="button"
									className={styles.bookButton}
									disabled={!selected || submitting}
									onClick={handleBook}
								>
									{submitting
										? t("booking.submitting")
										: t("booking.bookButton")}
								</button>
							</>
						)}
					</div>
				</div>
			</div>
		</>
	);
}
